import React from 'react';
import { UnsavedChangesDialog, type UnsavedChangesChoice } from './UnsavedChangesDialog';

export interface UnsavedChangesDialogRequest {
  title: string;
  message: React.ReactNode;
  saveLabel?: string;
  discardLabel?: string;
  cancelLabel?: string;
  /** Overrides the wording shown while Save runs. */
  savingLabel?: string;
  /** Overrides the wording shown while Discard runs. */
  discardingLabel?: string;
}

/** The two answers that start work; staying put starts nothing. */
export type UnsavedChangesCommit = Exclude<UnsavedChangesChoice, 'cancel'>;

export type UnsavedChangesRunner<T> = (choice: UnsavedChangesCommit) => Promise<T>;

export interface UnsavedChangesRunResult<T> {
  choice: UnsavedChangesChoice;
  /** Undefined when the user stayed, so nothing ran. */
  result: T | undefined;
}

export interface UnsavedChangesDialogApi {
  requestChoice: (request: UnsavedChangesDialogRequest) => Promise<UnsavedChangesChoice>;
  runChoice: <T>(
    request: UnsavedChangesDialogRequest,
    run: UnsavedChangesRunner<T>,
  ) => Promise<UnsavedChangesRunResult<T>>;
  unsavedChangesDialog: React.ReactNode;
}

const DefaultBusyPhase: Record<UnsavedChangesCommit, string> = {
  save: 'Saving your changes…',
  discard: 'Restoring your last saved setup…',
};

/** Says what is happening now, in the caller's words when it supplied any. */
const resolveBusyPhase = (
  request: UnsavedChangesDialogRequest,
  choice: UnsavedChangesCommit,
): string => {
  const supplied = choice === 'save' ? request.savingLabel : request.discardingLabel;
  return supplied ?? DefaultBusyPhase[choice];
};

type PendingRequest = {
  request: UnsavedChangesDialogRequest;
  run?: UnsavedChangesRunner<unknown>;
  settle: (outcome: UnsavedChangesRunResult<unknown>) => void;
  fail: (error: unknown) => void;
};

const StayPut: UnsavedChangesRunResult<never> = { choice: 'cancel', result: undefined };

/**
 * Asks the user what to do with unsaved changes and, when they choose an
 * action, keeps the dialog on screen naming the phase until that action
 * finishes. One question at a time: while an answer is outstanding, any further
 * request is answered as "stay here" rather than replacing what the user sees.
 */
export function useUnsavedChangesDialog(): UnsavedChangesDialogApi {
  const [pending, setPending] = React.useState<PendingRequest | null>(null);
  const [busyPhase, setBusyPhase] = React.useState<string | null>(null);
  // Read and written in the same tick as a click, so two clicks in one tick
  // cannot both start work; state alone would still be the old value.
  const pendingRef = React.useRef<PendingRequest | null>(null);
  const busyRef = React.useRef(false);

  const closePending = React.useCallback(() => {
    pendingRef.current = null;
    busyRef.current = false;
    setPending(null);
    setBusyPhase(null);
  }, []);

  const ask = React.useCallback(
    (request: UnsavedChangesDialogRequest, run?: UnsavedChangesRunner<unknown>) => (
      new Promise<UnsavedChangesRunResult<unknown>>((settle, fail) => {
        if (pendingRef.current) {
          settle(StayPut);
          return;
        }

        const next: PendingRequest = { request, run, settle, fail };
        pendingRef.current = next;
        setPending(next);
      })
    ),
    [],
  );

  const runPending = React.useCallback(
    async (current: PendingRequest, choice: UnsavedChangesCommit) => {
      busyRef.current = true;
      setBusyPhase(resolveBusyPhase(current.request, choice));

      let outcome: { ok: true; result: unknown } | { ok: false; error: unknown };
      try {
        outcome = { ok: true, result: await current.run!(choice) };
      } catch (error) {
        outcome = { ok: false, error };
      }

      closePending();
      if (outcome.ok) {
        current.settle({ choice, result: outcome.result });
      } else {
        // A failed save is the user's problem to see, not ours to swallow.
        current.fail(outcome.error);
      }
    },
    [closePending],
  );

  const choose = React.useCallback(
    (choice: UnsavedChangesChoice) => {
      const current = pendingRef.current;
      if (!current || busyRef.current) {
        return;
      }

      if (choice === 'cancel' || !current.run) {
        closePending();
        current.settle({ choice, result: undefined });
        return;
      }

      void runPending(current, choice);
    },
    [closePending, runPending],
  );

  const requestChoice = React.useCallback(
    (request: UnsavedChangesDialogRequest) => ask(request).then((outcome) => outcome.choice),
    [ask],
  );

  const runChoice = React.useCallback(
    <T,>(request: UnsavedChangesDialogRequest, run: UnsavedChangesRunner<T>) => (
      ask(request, run as UnsavedChangesRunner<unknown>) as Promise<UnsavedChangesRunResult<T>>
    ),
    [ask],
  );

  const unsavedChangesDialog = React.useMemo(() => (
    <UnsavedChangesDialog
      open={Boolean(pending)}
      title={pending?.request.title ?? ''}
      message={pending?.request.message ?? ''}
      saveLabel={pending?.request.saveLabel}
      discardLabel={pending?.request.discardLabel}
      cancelLabel={pending?.request.cancelLabel}
      busyPhase={busyPhase}
      onSave={() => choose('save')}
      onDiscard={() => choose('discard')}
      onCancel={() => choose('cancel')}
    />
  ), [busyPhase, choose, pending]);

  return { requestChoice, runChoice, unsavedChangesDialog };
}
